/**
 * Fechas de emision que se ofrecen al timbrar (CRM-3108).
 *
 * El backend solo acepta las ultimas 72 horas. Contadas desde ahora hacia
 * atras eso toca 4 fechas de calendario (hoy y los 3 dias completos
 * anteriores), no 3: 72 horas no es lo mismo que "3 dias de calendario".
 * Los dias previos se timbran al cierre del dia.
 *
 * NOTA: CRM-3073 trae un archivo con este mismo nombre y una ventana de 3
 * dias. Cuando ese MR entre a master el conflicto es deliberado: hay que
 * quedarse con esta version parametrizada, no con las dos.
 */

export const ISSUE_WINDOW_HOURS = 72;
export const HOURS_PER_DAY = 24;
export const PAST_DAY_TIME = "23:59:59";
// Margen para que la fecha mas antigua no quede al limite de las 72 h
// mientras el usuario confirma y la peticion llega al backend.
export const WINDOW_SAFETY_MARGIN_MINUTES = 5;

const pad = (value) => String(value).padStart(2, "0");

const toDisplay = (date) =>
  `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;

const toValue = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const dayLabel = (daysAgo) => {
  if (daysAgo === 0) return "(Hoy)";
  if (daysAgo === 1) return "(Ayer)";
  return "";
};

const startOfDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Dias calendario que toca la ventana de horas hacia atras desde `now`.
 * Con 72 horas exactas la resta de calendario a calendario da 3 (hoy menos
 * 3 dias completos), pero la ventana SI toca ese cuarto dia -de "ahora" a
 * "hace 72 horas" son cuatro fechas distintas-, por eso se suma 1: no es
 * una simple division de horas entre 24.
 */
export const countWindowDays = (now, windowHours) => {
  const windowStart = new Date(now);
  windowStart.setHours(windowStart.getHours() - windowHours);
  const diffDays = Math.round(
    (startOfDay(now) - startOfDay(windowStart)) / MS_PER_DAY,
  );
  return diffDays + 1;
};

/**
 * Opciones para el Autocomplete, de la mas reciente a la mas antigua.
 * La de hoy se timbra con la hora actual; las anteriores a las 23:59:59.
 */
export const getIssueDateOptions = (now = new Date()) => {
  const days = countWindowDays(now, ISSUE_WINDOW_HOURS);

  const limit =
    now.getTime() -
    (ISSUE_WINDOW_HOURS * 60 - WINDOW_SAFETY_MARGIN_MINUTES) * 60 * 1000;

  const options = Array.from({ length: days }, (_, daysAgo) => {
    const date = new Date(now);
    date.setDate(date.getDate() - daysAgo);

    const isToday = daysAgo === 0;
    const time = isToday
      ? `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`
      : PAST_DAY_TIME;

    const suffix = dayLabel(daysAgo);
    const label = isToday
      ? `${toDisplay(date)} ${suffix}`.trim()
      : `${toDisplay(date)} ${suffix} ${PAST_DAY_TIME}`.replace(/\s+/g, " ").trim();

    return {
      id: toValue(date),
      value: toValue(date),
      label,
      date: `${toValue(date)} ${time}`,
      timestamp: isToday
        ? now.getTime()
        : new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            23,
            59,
            59,
          ).getTime(),
    };
  });

  return options
    .filter(({ timestamp }) => timestamp >= limit)
    .map(({ timestamp: _timestamp, ...option }) => option);
};

export const findIssueDateOption = (options = [], id) =>
  options.find((option) => option.id === id) || null;
